// pages/VoiceRecording/VoiceList.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import EditVoice from "./EditVoice";
import backendURL from "../../config";
import RetrainButton from "../Dashboard/RetrainButton"
import { Modal as MuiModal, Box, Tooltip, Button as MuiButton } from '@mui/material'; 

export default function VoiceList({ refresh }) {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [searchText, setSearchText] = useState(""); // State for search input

  useEffect(() => {
    document.title = "Upload Voice | KlonIT Web Application";
    fetchDocuments();
  }, [refresh, triggerFetch]);

  useEffect(() => {
    // Filter documents based on search text
    const filtered = documents.filter((doc) =>
      Object.values(doc)
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
    setFilteredDocuments(filtered);
  }, [searchText, documents]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.get(`${backendURL}/get-voices`, { headers });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;


      setDocuments(responseData);
      setFilteredDocuments(responseData);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.error("Authentication error:", error.response.data.message);
        setError("Authentication error:", error.response.data.message);
      } else {
        console.error("Error fetching documents:", error);
        setError("Error fetching documents:", error);
      }
    }
  };

  const handleDelete = async (documentId) => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      await axios.delete(`${backendURL}/delete-voice/${documentId}`, { headers });
      setDocuments(documents.filter((doc) => doc.id !== documentId));
      setError("Document has been deleted successfully.");
    } catch (error) {
      console.error("Error deleting voice data:", error);
      setError("Error deleting voice data:", error);
    }
  };

  const handleEdit = (document) => {
    setCurrentDocument(document);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentDocument(null);
    setTriggerFetch(!triggerFetch);
  };

  const updateModal = () => {
    fetchDocuments();
    closeModal();
  };


  const columns = [
    { name: "File Name", selector: (row) => row.filename, sortable: true, maxWidth: "200px" },
    { name: "Page Content", selector: (row) => row.content, sortable: true, maxWidth: "200px" },
    { name: "Summary", selector: (row) => row.summary, sortable: true, maxWidth: "200px" },
    { name: "Is Scrape?", selector: (row) => (row.is_scrape ? "Yes" : "No"), sortable: true, maxWidth: "125px" },
    { name: "Is Trained?", selector: (row) => (row.is_trained ? "Yes" : "No"), sortable: true, maxWidth: "125px" },
    { name: "Upload Date", selector: (row) => row.upload_date, sortable: true, maxWidth: "150px" },
    {
      name: "Action",
      cell: (row) => (
        <>
          <button className="deleteIcon" onClick={() => handleDelete(row.id)}>
            <i className="fa fa-trash-o fa-2x"></i>
          </button>
          <button className="EditIcon" onClick={() => handleEdit(row)}>
            <i className="fa fa-pencil fa-2x"></i>
          </button>
        </>
      ),
    },
  ];

  const ExpandedComponent = ({ data }) => (
    <div>
      <p><strong>File Name:</strong> {data.filename}</p>
      <p><strong>Summary:</strong> {data.summary}</p>
      <p><strong>Page Content:</strong> {data.content}</p>      
    </div>
  );

  
  
  // Custom Styles for the MUI Modal (using sx prop)
  const muiModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: '80%', md: '900px' }, // Responsive width, md increased from 700px
    maxWidth: '90vw', // Ensures modal is not wider than 90% of viewport width
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    border: 'none', // MUI Box handles border/shadow via elevation or sx
    borderRadius: '8px',
    boxShadow: 24, // MUI elevation for shadow
    p: 3, // Padding (theme.spacing(3))
    display: 'flex',
    flexDirection: 'column',
    outline: 'none', // Remove default focus outline
  };
  const EditModalStyle = {
    content: {
      width: "85%",
      height: "85%",
      margin: "auto",
      padding: "20px",
      borderRadius: "10px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  };

  return (
    <div className="row">
      <div className="col-12 col-lg-12 col-xxl-12 d-flex">
        <div className="card flex-fill">
        <div className="card-header">
          
              

              <div className="row">
                <div className="col-lg-4">

                </div>
                <div className="col-lg-4">
                  <div className="mt-2">
                    <RetrainButton/>
                  </div>                                    
                </div>
                <div className="col-lg-4">
                  <input
                    type="text"
                    placeholder="Search voice..."
                    className="form-control mb-3"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>
              
            </div>
          <div className="card-body">            
            {loading ? (
              <p>Loading...</p>
            ) : filteredDocuments.length === 0 ? (
              <p>No data available</p>
            ) : (
              <DataTable
                columns={columns}
                data={filteredDocuments}
                pagination
                responsive
                highlightOnHover
                pointerOnHover
                expandableRows
                expandableRowsComponent={({ data }) => <ExpandedComponent data={data} />}
                expandOnRowClicked
              />
            )}
            <br />
            <div id="getError" className={`alert alert-warning ${error ? "" : "d-none"}`}>
              {error}
            </div>
            <br />
          </div>
        </div>
      </div>
        
      <MuiModal
              open={isModalOpen}
              onClose={closeModal} // Handles backdrop click and Escape key
              aria-labelledby="monetize-modal-title"
              aria-describedby="monetize-modal-description"
              closeAfterTransition // Better for animations
              // BackdropComponent={Backdrop} // Optional: for custom backdrop
              // BackdropProps={{ timeout: 500 }} // Optional: for custom backdrop
            >
            <Box sx={muiModalStyle}>
                <EditVoice document={currentDocument} onClose={closeModal} onUpdate={updateModal} />
            </Box>
      </MuiModal>
    </div>
  );
}
